import { SUPPLY_TO_SHOPPING_CATEGORY } from "../data/suppliesData";

/* ════════════════════════════════════════════════════════════════════════
   SUPPLIES UTILS — pure helpers operating over the suppliesItems array
   (and optionally pantryItems, for backwards-compatible Meals pantry).
   All functions are side-effect free. State writes happen in the provider.
   ════════════════════════════════════════════════════════════════════════ */

const LOW_LIKE = new Set(["Low"]);
const NEEDS_BUYING = new Set(["Low", "Out"]);
const USE_SOON_LIKE = new Set(["Use soon", "Expired"]);

/* Strictly "Low" status. */
export function getLowStockItems(suppliesItems = []) {
  return (suppliesItems || []).filter(it => LOW_LIKE.has(it.status));
}

/* Low + Out — items that need to be bought. */
export function getNeedsBuyingItems(suppliesItems = []) {
  return (suppliesItems || []).filter(it => NEEDS_BUYING.has(it.status));
}

export function getOutItems(suppliesItems = []) {
  return (suppliesItems || []).filter(it => it.status === "Out");
}

export function getUseSoonItems(suppliesItems = []) {
  return (suppliesItems || []).filter(it => it.status === "Use soon");
}

export function getExpiredItems(suppliesItems = []) {
  return (suppliesItems || []).filter(it => it.status === "Expired");
}

export function getItemsByCategory(suppliesItems = [], category) {
  if (!category || category === "All") return suppliesItems || [];
  return (suppliesItems || []).filter(it => it.category === category);
}

/* Build a flat summary of low-stock counts grouped by category bucket. */
export function getSuppliesSummary(suppliesItems = []) {
  const low = getLowStockItems(suppliesItems);
  const out = getOutItems(suppliesItems);
  const needsBuying = getNeedsBuyingItems(suppliesItems);
  const useSoon = getUseSoonItems(suppliesItems);
  const expired = getExpiredItems(suppliesItems);

  const countByCategory = (items) => items.reduce((acc, it) => {
    acc[it.category] = (acc[it.category] || 0) + 1;
    return acc;
  }, {});

  return {
    totalItems: suppliesItems.length,
    lowCount: low.length,
    outCount: out.length,
    useSoonCount: useSoon.length,
    expiredCount: expired.length,
    needsBuying: needsBuying.length, // headline number
    byCategoryLow: countByCategory(low),
    byCategoryOut: countByCategory(out),
  };
}

/* Estimated cost of replenishing all currently low / out items. */
export function estimateSuppliesCost(suppliesItems = []) {
  return getNeedsBuyingItems(suppliesItems).reduce((sum, it) => sum + (Number(it.estimatedCost) || 0), 0);
}

/* Build a list of shopping items based on low/out supplies.
   Skips items where autoShop === false. */
export function buildShoppingListFromLowStock(suppliesItems = []) {
  return getNeedsBuyingItems(suppliesItems)
    .filter(it => it.autoShop !== false)
    .map(it => ({
      item: it.name + (it.brand ? ` (${it.brand})` : ""),
      category: SUPPLY_TO_SHOPPING_CATEGORY[it.category] || "Other",
      quantity: it.unit ? `1 ${it.unit}` : "",
      sourceSupplyId: it.id,
    }));
}

/* Merge a meal-plan derived shopping list with low-stock supply items.
   Dedupes by lowercase item name. */
export function mergeMealPlanShoppingListWithSupplies(existingShopping = [], suppliesItems = []) {
  const map = new Map();
  (existingShopping || []).forEach(it => {
    const key = String(it.item || "").toLowerCase().trim();
    if (key) map.set(key, it);
  });
  buildShoppingListFromLowStock(suppliesItems).forEach(addition => {
    const key = addition.item.toLowerCase().trim();
    if (!map.has(key)) map.set(key, { ...addition, checked: false });
  });
  return Array.from(map.values());
}

/* Restock a set of supply ids — returns updated list. */
export function markItemsRestocked(suppliesItems = [], ids = []) {
  const idSet = new Set(ids);
  const now = new Date().toISOString();
  return (suppliesItems || []).map(it =>
    idSet.has(it.id)
      ? { ...it, status: "Good", quantity: it.quantity > 0 ? it.quantity : 1, lastUpdated: now }
      : it
  );
}

/* Friendly status badge label. */
export function statusBadge(status) {
  if (status === "Out") return "Out";
  if (status === "Low") return "Low";
  if (status === "Use soon") return "Use soon";
  if (status === "Expired") return "Expired";
  return "Good";
}

/* Optional adapter — merges the legacy Meals `pantryItems` shape
   (item, category, quantity, status) into the new supplies shape.
   Useful if a consumer wants both data sources in one list. */
export function adaptPantryItem(p) {
  return {
    id: p.id,
    name: p.item || p.name || "Item",
    category: "Pantry",
    location: "Pantry",
    quantity: p.quantity || "",
    unit: "",
    status: p.status || "Good",
    brand: "",
    estimatedCost: 0,
    autoShop: false,
    notes: p.notes || "",
    lastUpdated: p.lastUpdated || new Date().toISOString(),
    _source: "pantry",
  };
}
